import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, MessageCircle, Trash2, UserPlus, UserCheck, Crown, ArrowLeft, Send, Ticket } from "lucide-react";

const TIER_BADGE = {
  free: "bg-white/10 text-neutral-400",
  pro: "bg-[#a855f7]/20 text-[#a855f7]",
  vip: "bg-[#22c55e]/20 text-[#22c55e]",
};

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Author({ author, onOpen }) {
  return (
    <button onClick={() => onOpen(author.id)} className="flex items-center gap-2 group" data-testid={`post-author-${author.id}`}>
      <div className="w-9 h-9 rounded-full bg-[#a855f7]/20 border border-[#a855f7]/40 flex items-center justify-center font-display font-bold text-[#a855f7]">
        {author.name?.[0]?.toUpperCase()}
      </div>
      <div className="text-left">
        <div className="text-sm font-semibold text-white group-hover:text-[#a855f7] transition-colors duration-200">
          {author.name} {author.role === "admin" && <Crown size={12} className="inline text-[#a855f7]" />}
        </div>
        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${TIER_BADGE[author.tier] || TIER_BADGE.free}`}>
          {author.role === "admin" ? "admin" : author.tier}
        </span>
      </div>
    </button>
  );
}

function Comments({ postId }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const { data } = useQuery({ queryKey: ["comments", postId], queryFn: () => api.get(`/community/posts/${postId}/comments`).then((r) => r.data) });
  const add = useMutation({
    mutationFn: () => api.post(`/community/posts/${postId}/comments`, { content: text }),
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["comments", postId] }); qc.invalidateQueries({ queryKey: ["feed"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="mt-3 border-t border-white/10 pt-3 space-y-3">
      {(data?.comments || []).map((c) => (
        <div key={c.id} className="flex gap-2 text-sm" data-testid={`comment-${c.id}`}>
          <span className="font-semibold text-[#a855f7] shrink-0">{c.author.name}:</span>
          <span className="text-neutral-300">{c.content}</span>
          <span className="text-neutral-600 text-xs ml-auto shrink-0">{timeAgo(c.created_at)}</span>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment..."
          onKeyDown={(e) => e.key === "Enter" && text.trim() && add.mutate()}
          data-testid={`comment-input-${postId}`}
          className="flex-1 bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-1.5 text-sm outline-none transition-colors duration-200" />
        <button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid={`comment-submit-${postId}`}
          className="bg-white/10 hover:bg-[#a855f7] hover:text-black rounded px-3 transition-colors duration-200 disabled:opacity-40"><Send size={14} /></button>
      </div>
    </div>
  );
}

function PostCard({ post, onOpenProfile, canModerate }) {
  const qc = useQueryClient();
  const [showComments, setShowComments] = useState(false);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["feed"] }); qc.invalidateQueries({ queryKey: ["profile"] }); };
  const like = useMutation({ mutationFn: () => api.post(`/community/posts/${post.id}/like`), onSuccess: invalidate });
  const del = useMutation({ mutationFn: () => api.delete(`/community/posts/${post.id}`), onSuccess: () => { invalidate(); toast.success("Post deleted"); } });
  const follow = useMutation({
    mutationFn: () => api.post(`/community/follow/${post.author.id}`),
    onSuccess: (r) => { invalidate(); toast.success(r.data.following ? `Following ${post.author.name}` : `Unfollowed ${post.author.name}`); },
  });

  return (
    <div className="card-surface rounded-lg p-4" data-testid={`post-card-${post.id}`}>
      <div className="flex items-start justify-between">
        <Author author={post.author} onOpen={onOpenProfile} />
        <div className="flex items-center gap-3">
          {!post.is_own && (
            <button onClick={() => follow.mutate()} data-testid={`follow-btn-${post.id}`}
              className={`flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded border transition-colors duration-200 ${post.is_following ? "border-[#22c55e]/50 text-[#22c55e]" : "border-white/20 text-neutral-400 hover:border-[#a855f7] hover:text-[#a855f7]"}`}>
              {post.is_following ? <UserCheck size={12} /> : <UserPlus size={12} />}
              {post.is_following ? "Following" : "Follow"}
            </button>
          )}
          <span className="text-xs text-neutral-600">{timeAgo(post.created_at)}</span>
        </div>
      </div>
      {post.kind === "parlay" && post.legs?.length > 0 && (
        <div className="mt-3 border border-[#22c55e]/30 bg-[#22c55e]/5 rounded-lg p-3" data-testid={`parlay-slip-${post.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#22c55e] flex items-center gap-1.5"><Ticket size={12} /> Parlay Slip · {post.legs.length} legs</span>
            {post.odds && <span className="font-data text-sm font-bold text-[#22c55e]">{post.odds}</span>}
          </div>
          <div className="mt-2 space-y-1">
            {post.legs.map((leg, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-neutral-200">
                <span className="font-data text-[10px] text-[#22c55e] w-4">{i + 1}</span>{leg}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-sm text-neutral-200 mt-3 whitespace-pre-wrap">{post.content}</p>
      <div className="flex items-center gap-4 mt-3">
        <button onClick={() => like.mutate()} data-testid={`like-btn-${post.id}`}
          className={`flex items-center gap-1.5 text-sm transition-colors duration-200 ${post.liked ? "text-red-400" : "text-neutral-500 hover:text-red-400"}`}>
          <Heart size={16} fill={post.liked ? "currentColor" : "none"} /> {post.like_count}
        </button>
        <button onClick={() => setShowComments(!showComments)} data-testid={`comments-toggle-${post.id}`}
          className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#a855f7] transition-colors duration-200">
          <MessageCircle size={16} /> {post.comment_count}
        </button>
        {(post.is_own || canModerate) && (
          <button onClick={() => del.mutate()} data-testid={`delete-post-${post.id}`}
            className="ml-auto text-neutral-700 hover:text-red-400 transition-colors duration-200"><Trash2 size={15} /></button>
        )}
      </div>
      {showComments && <Comments postId={post.id} />}
    </div>
  );
}

function Profile({ userId, onBack, onOpenProfile }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["profile", userId], queryFn: () => api.get(`/community/profile/${userId}`).then((r) => r.data) });
  const follow = useMutation({
    mutationFn: () => api.post(`/community/follow/${userId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["profile", userId] }); qc.invalidateQueries({ queryKey: ["feed"] }); },
  });
  if (!data) return <div className="text-neutral-500 font-data text-sm animate-pulse">Loading profile...</div>;
  const u = data.user;
  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="profile-back-btn" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors duration-200">
        <ArrowLeft size={15} /> Back to feed
      </button>
      <div className="card-gold border border-[#a855f7]/30 rounded-xl p-6 gold-glow flex flex-wrap items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-[#a855f7]/20 border-2 border-[#a855f7]/50 flex items-center justify-center font-display text-2xl font-bold text-[#a855f7]">
          {u.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <h2 className="font-display text-2xl font-black uppercase" data-testid="profile-name">{u.name} {u.role === "admin" && <Crown size={16} className="inline text-[#a855f7]" />}</h2>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${TIER_BADGE[u.tier]}`}>{u.role === "admin" ? "admin" : u.tier}</span>
          <div className="flex gap-5 mt-2 font-data text-sm">
            <span data-testid="profile-followers"><b className="text-white">{data.followers}</b> <span className="text-neutral-500">followers</span></span>
            <span data-testid="profile-following"><b className="text-white">{data.following}</b> <span className="text-neutral-500">following</span></span>
            <span><b className="text-white">{data.posts.length}</b> <span className="text-neutral-500">posts</span></span>
          </div>
        </div>
        {!data.is_own && (
          <button onClick={() => follow.mutate()} data-testid="profile-follow-btn"
            className={`flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-5 py-2.5 rounded transition-colors duration-200 ${data.is_following ? "border border-[#22c55e]/50 text-[#22c55e]" : "bg-[#a855f7] hover:bg-[#9333ea] text-black"}`}>
            {data.is_following ? <UserCheck size={14} /> : <UserPlus size={14} />}
            {data.is_following ? "Following" : "Follow"}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {data.posts.map((p) => <PostCard key={p.id} post={p} onOpenProfile={onOpenProfile} canModerate={user?.role === "admin"} />)}
        {data.posts.length === 0 && <p className="text-neutral-600 text-sm text-center py-6">No posts yet.</p>}
      </div>
    </div>
  );
}

export default function Community() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [channel, setChannel] = useState("main");
  const [feed, setFeed] = useState("all");
  const [text, setText] = useState("");
  const [profileId, setProfileId] = useState(null);
  const [mode, setMode] = useState("text");
  const [legs, setLegs] = useState(["", ""]);
  const [odds, setOdds] = useState("");
  const vipAllowed = user?.tier === "vip" || user?.role === "admin";

  const { data, error } = useQuery({
    queryKey: ["feed", channel, feed],
    queryFn: () => api.get(`/community/posts?channel=${channel}&feed=${feed}`).then((r) => r.data),
    enabled: channel === "main" || vipAllowed,
  });

  const post = useMutation({
    mutationFn: () => api.post("/community/posts", {
      content: mode === "parlay" ? (text.trim() || "Tail or fade 👇") : text,
      channel, kind: mode,
      legs: mode === "parlay" ? legs.filter((leg) => leg.trim()) : [],
      odds: mode === "parlay" ? odds : "",
    }),
    onSuccess: () => { setText(""); setLegs(["", ""]); setOdds(""); qc.invalidateQueries({ queryKey: ["feed"] }); toast.success(mode === "parlay" ? "Parlay posted — let them tail" : "Posted"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const canPost = mode === "parlay" ? legs.some((leg) => leg.trim()) : text.trim();

  if (profileId) {
    return (
      <div className="max-w-2xl space-y-5">
        <Profile userId={profileId} onBack={() => setProfileId(null)} onOpenProfile={setProfileId} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Commu<span className="gold-text">nity</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Post your slips, tail the sharps, talk baseball.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 fade-up-1">
        <div className="flex card-surface rounded p-0.5">
          <button onClick={() => setChannel("main")} data-testid="channel-main-tab"
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded transition-colors duration-200 ${channel === "main" ? "bg-[#a855f7] text-black" : "text-neutral-400 hover:text-white"}`}>Main</button>
          <button onClick={() => setChannel("vip")} data-testid="channel-vip-tab"
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded transition-colors duration-200 ${channel === "vip" ? "bg-[#22c55e] text-black" : "text-neutral-400 hover:text-white"}`}>VIP</button>
        </div>
        <div className="flex card-surface rounded p-0.5 ml-auto">
          <button onClick={() => setFeed("all")} data-testid="feed-all-tab"
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded transition-colors duration-200 ${feed === "all" ? "bg-white/15 text-white" : "text-neutral-400 hover:text-white"}`}>All</button>
          <button onClick={() => setFeed("following")} data-testid="feed-following-tab"
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded transition-colors duration-200 ${feed === "following" ? "bg-white/15 text-white" : "text-neutral-400 hover:text-white"}`}>Following</button>
        </div>
      </div>

      {channel === "vip" && !vipAllowed ? (
        <div className="card-gold border border-[#22c55e]/30 rounded-lg p-8 text-center" data-testid="vip-gate">
          <Crown className="mx-auto text-[#22c55e] mb-3" size={26} />
          <h3 className="font-display text-2xl font-bold uppercase">VIP Channel</h3>
          <p className="text-neutral-400 text-sm mt-2">Premium alerts, early boards and sharp-only talk. Upgrade to VIP ($79/mo) in Settings.</p>
        </div>
      ) : (
        <>
          <div className="card-surface rounded-lg p-4 fade-up-2">
            <div className="flex gap-1.5 mb-3">
              <button onClick={() => setMode("text")} data-testid="compose-mode-text"
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border transition-colors duration-200 ${mode === "text" ? "border-[#a855f7] text-[#a855f7] bg-[#a855f7]/10" : "border-white/15 text-neutral-500 hover:text-white"}`}>Post</button>
              <button onClick={() => setMode("parlay")} data-testid="compose-mode-parlay"
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border transition-colors duration-200 ${mode === "parlay" ? "border-[#22c55e] text-[#22c55e] bg-[#22c55e]/10" : "border-white/15 text-neutral-500 hover:text-white"}`}>
                <Ticket size={11} className="inline mr-1" />Parlay Slip
              </button>
            </div>
            {mode === "parlay" && (
              <div className="space-y-2 mb-3" data-testid="parlay-composer">
                {legs.map((leg, i) => (
                  <input key={i} value={leg} onChange={(e) => setLegs(legs.map((v, j) => (j === i ? e.target.value : v)))}
                    placeholder={`Leg ${i + 1} — e.g. Judge To Hit a HR +320`} data-testid={`parlay-leg-input-${i}`}
                    className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#22c55e] rounded px-3 py-2 text-sm outline-none transition-colors duration-200" />
                ))}
                <div className="flex gap-2">
                  <button onClick={() => legs.length < 10 && setLegs([...legs, ""])} data-testid="parlay-add-leg-btn"
                    className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 border border-white/15 hover:border-[#22c55e] hover:text-[#22c55e] px-2.5 py-1 rounded transition-colors duration-200">+ Add leg</button>
                  <input value={odds} onChange={(e) => setOdds(e.target.value)} placeholder="Total odds e.g. +1240" data-testid="parlay-odds-input"
                    className="w-36 bg-[#0a0a0a] border border-white/15 focus:border-[#22c55e] rounded px-3 py-1 text-xs outline-none transition-colors duration-200" />
                </div>
              </div>
            )}
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={1000}
              placeholder={mode === "parlay" ? "Say something about this slip (optional)..." : channel === "vip" ? "Post to the VIP channel..." : "What's cooking? Share a slip, a win, a take..."}
              data-testid="post-composer-input"
              className="w-full bg-transparent text-sm text-white placeholder:text-neutral-600 outline-none resize-none" />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-neutral-600 font-data">{text.length}/1000</span>
              <button onClick={() => post.mutate()} disabled={!canPost || post.isPending} data-testid="post-submit-btn"
                className={`${mode === "parlay" ? "bg-[#22c55e] hover:bg-[#16a34a]" : "bg-[#a855f7] hover:bg-[#9333ea]"} disabled:opacity-40 text-black font-bold uppercase tracking-wider text-xs px-5 py-2 rounded transition-colors duration-200`}>
                {mode === "parlay" ? "Post Parlay" : "Post"}
              </button>
            </div>
          </div>

          <div className="space-y-3 fade-up-3">
            {(data?.posts || []).map((p) => (
              <PostCard key={p.id} post={p} onOpenProfile={setProfileId} canModerate={user?.role === "admin"} />
            ))}
            {data?.posts?.length === 0 && (
              <p className="text-neutral-600 text-sm text-center py-8" data-testid="feed-empty">
                {feed === "following" ? "Nothing from people you follow yet. Follow some sharps from the All feed." : "No posts yet. Be the first."}
              </p>
            )}
            {error && <p className="text-red-400 text-sm text-center py-4">{apiError(error)}</p>}
          </div>
        </>
      )}
    </div>
  );
}
